import { Diagnostic, DiagnosticSeverity } from 'vscode-languageserver-protocol';
import {
  collectSimplePythonForTargetNames,
  collectModuleFlowStatements,
  extractInlineSuiteText,
  findTopLevelKeywordAssignmentIndex,
  isOffsetInPythonStringOrLineComment,
  parseSimplePythonAssignment,
  readBalancedDelimiterSpan,
  replacePythonStringContentsWithSpaces,
  splitCodeLines,
  splitTopLevelByComma,
  splitTopLevelBySemicolon,
  stripTrailingPythonLineComment,
  type PythonSourceTextSegment,
  type PythonModuleFlowStatement,
} from '../../../utils/pythonSourceScanUtils';

export const LINEAR_MECHANISM_VELOCITY_DIAGNOSTIC_SOURCE = 'bps';
export const LINEAR_MECHANISM_VELOCITY_DIAGNOSTIC_CODE = 'linear-mechanism-velocity-source-units';

export const LINEAR_MECHANISM_VELOCITY_LINT_MESSAGE_DEFAULTS = {
  motorRequiresRotational: 'Linear Mechanism velocity measured at the motor must use rotational velocity units.',
  mechanismOutputRequiresLinear: 'Linear Mechanism velocity measured at the mechanism output must use linear velocity units.',
} as const;

export type LinearMechanismVelocityLintMessages = {
  [K in keyof typeof LINEAR_MECHANISM_VELOCITY_LINT_MESSAGE_DEFAULTS]: string;
};

const LINEAR_MECHANISM_CLASS_NAMES = new Set(['LinearMechanism']);
const SOURCE_AWARE_VELOCITY_ACTIONS = new Set([
  'set_velocity_action',
  'on_until_beam_true_action',
  'on_until_distance_is_action',
  'on_until_magnet_is_detected_action',
  'print_to_screen_velocity_live_action',
  'add_variable_velocity_control',
]);
const DIRECT_METHOD_CALL_RE = /\b([A-Za-z_][\w]*)\s*\.\s*([A-Za-z_][\w]*)\s*\(/g;
const MOTOR_SOURCE_RE = /^MeasuredAt\s*\.\s*MOTOR$/;
const MECHANISM_OUTPUT_SOURCE_RE = /^MeasuredAt\s*\.\s*MECHANISM_OUTPUT$/;
const LINEAR_VELOCITY_UNIT_RE = /^LinearVelocityUnits\s*\.\s*[A-Za-z_][\w]*$/;
const ROTATIONAL_VELOCITY_UNIT_RE = /^RotationalVelocityUnits\s*\.\s*[A-Za-z_][\w]*$/;
const MODULE_FLOW_BLOCK_KINDS = new Set(['if', 'elif', 'else', 'for', 'while', 'with', 'try', 'except', 'finally']);

interface ModuleFlowStatementContext {
  statement: PythonModuleFlowStatement;
  statementText: string;
  ancestorStartLineIndexes: number[];
  inlineSuiteText: string | null;
  inlineSuiteSegments: PythonSourceTextSegment[];
}

function getLogicalStatementText(lines: string[], statement: PythonModuleFlowStatement): string {
  return lines
    .slice(statement.startLineIndex, statement.endLineIndex + 1)
    .map((line) => stripTrailingPythonLineComment(line.trim()))
    .join(' ');
}

function collectModuleFlowStatementContexts(code: string, lines: string[]): ModuleFlowStatementContext[] {
  const contexts: ModuleFlowStatementContext[] = [];
  const blockStack: ModuleFlowStatementContext[] = [];

  for (const statement of collectModuleFlowStatements(code)) {
    if (statement.insideDefOrClass) continue;
    while (blockStack.length > 0 && (blockStack.at(-1)?.statement.indentColumn ?? -1) >= statement.indentColumn) {
      blockStack.pop();
    }
    const statementText = getLogicalStatementText(lines, statement);
    const inlineSuiteText = MODULE_FLOW_BLOCK_KINDS.has(statement.kind) ? extractInlineSuiteText(statementText) : null;
    const context = {
      statement,
      statementText,
      ancestorStartLineIndexes: blockStack.map(({ statement: ancestor }) => ancestor.startLineIndex),
      inlineSuiteText,
      inlineSuiteSegments: inlineSuiteText === null ? [] : splitTopLevelBySemicolon(inlineSuiteText),
    };
    contexts.push(context);
    if (MODULE_FLOW_BLOCK_KINDS.has(statement.kind) && inlineSuiteText === null) {
      blockStack.push(context);
    }
  }

  return contexts;
}

function isPathPrefix(prefix: number[], path: number[]): boolean {
  return prefix.length <= path.length && prefix.every((lineIndex, index) => lineIndex === path[index]);
}

/**
 * Treats unrecognized receiver references as proof barriers. This deliberately
 * recognizes only member access and direct comparisons as non-binding reads;
 * every other occurrence remains unknown instead of growing a Python binder parser.
 */
function hasUnprovenReceiverReference(statementText: string, receiverName: string): boolean {
  const scannable = replacePythonStringContentsWithSpaces(statementText);
  const receiverReference = new RegExp(`\\b${receiverName}\\b`, 'g');
  let match: RegExpExecArray | null = null;

  while ((match = receiverReference.exec(scannable))) {
    const after = scannable.slice(match.index + receiverName.length);
    if (/^\s*\./.test(after)) continue;
    if (/^\s*(?:==|!=|<=|>=|<(?![<=])|>(?![>=]))/.test(after)) continue;
    return true;
  }

  return false;
}

/** Segment index for an inline-suite call; undefined before/outside the suite, null when offsets cannot be proven. */
function findInlineSuiteCallSegmentIndex(
  context: ModuleFlowStatementContext,
  lines: string[],
  callLineIndex: number,
  callCharacter: number
): number | undefined | null {
  const { statement, statementText, inlineSuiteText, inlineSuiteSegments } = context;
  if (inlineSuiteText === null) return undefined;
  if (statement.startLineIndex !== statement.endLineIndex || callLineIndex !== statement.startLineIndex) return null;

  const rawLine = lines[callLineIndex] ?? '';
  const leadingWhitespaceLength = rawLine.length - rawLine.trimStart().length;
  const callStatementIndex = callCharacter - leadingWhitespaceLength;
  const inlineSuiteStart = statementText.lastIndexOf(inlineSuiteText);
  if (inlineSuiteStart < 0) return null;
  const callInlineIndex = callStatementIndex - inlineSuiteStart;
  if (callInlineIndex < 0) return undefined;

  const segmentIndex = inlineSuiteSegments.findIndex(({ startIndex, endIndex }) => startIndex <= callInlineIndex && callInlineIndex < endIndex);
  return segmentIndex >= 0 ? segmentIndex : null;
}

function hasProvenLinearMechanismReceiver(
  receiverName: string,
  callLineIndex: number,
  callCharacter: number,
  lines: string[],
  contexts: ModuleFlowStatementContext[]
): boolean {
  const callContext = contexts.find(({ statement }) => statement.startLineIndex <= callLineIndex && statement.endLineIndex >= callLineIndex);
  if (!callContext) return false;
  const inlineCallSegmentIndex = findInlineSuiteCallSegmentIndex(callContext, lines, callLineIndex, callCharacter);
  if (inlineCallSegmentIndex === null) return false;
  const callPath =
    inlineCallSegmentIndex === undefined
      ? callContext.ancestorStartLineIndexes
      : [...callContext.ancestorStartLineIndexes, callContext.statement.startLineIndex];

  let binding: 'linear-mechanism' | 'unknown' = 'unknown';
  for (const context of contexts) {
    const { statement, statementText, ancestorStartLineIndexes, inlineSuiteText, inlineSuiteSegments } = context;
    if (statement.startLineIndex > callLineIndex) break;

    const isReceiverLoopTarget = statement.kind === 'for' && collectSimplePythonForTargetNames(statementText).has(receiverName);
    if (isReceiverLoopTarget) binding = 'unknown';

    const inlineSuiteStart = inlineSuiteText === null ? -1 : statementText.lastIndexOf(inlineSuiteText);
    const headerText = inlineSuiteStart < 0 ? statementText : statementText.slice(0, inlineSuiteStart);
    const statementParts = [
      {
        text: headerText,
        bindingPath: statement.kind === 'other' ? ancestorStartLineIndexes : [...ancestorStartLineIndexes, statement.startLineIndex],
      },
      ...inlineSuiteSegments
        .slice(0, context === callContext ? inlineCallSegmentIndex ?? 0 : undefined)
        .map(({ text }) => ({ text, bindingPath: [...ancestorStartLineIndexes, statement.startLineIndex] })),
    ];

    for (const { text, bindingPath } of statementParts) {
      const assignment = parseSimplePythonAssignment(text);
      if (assignment?.variableName !== receiverName) {
        if (hasUnprovenReceiverReference(text, receiverName)) binding = 'unknown';
        continue;
      }
      if (!isPathPrefix(bindingPath, callPath)) {
        binding = 'unknown';
        continue;
      }
      binding = assignment.constructorClassName && LINEAR_MECHANISM_CLASS_NAMES.has(assignment.constructorClassName) ? 'linear-mechanism' : 'unknown';
    }
  }

  return binding === 'linear-mechanism';
}

function isQualifiedReceiver(line: string, receiverStart: number): boolean {
  let previousIndex = receiverStart - 1;
  if (previousIndex >= 0 && /[\w]/.test(line[previousIndex] ?? '')) return true;
  while (previousIndex >= 0 && /\s/.test(line[previousIndex] ?? '')) previousIndex--;
  return line[previousIndex] === '.';
}

function getCodeLineStartOffsets(code: string): number[] {
  const offsets = [0];
  for (let index = 0; index < code.length; index++) {
    if (code[index] === '\n') offsets.push(index + 1);
  }
  return offsets;
}

function readExplicitKeywordArgs(argsText: string): Map<string, string> {
  const keywordArgs = new Map<string, string>();
  for (const arg of splitTopLevelByComma(argsText, { ignoreLineComments: true })) {
    const assignmentIndex = findTopLevelKeywordAssignmentIndex(arg);
    if (assignmentIndex === -1) continue;
    const name = arg.slice(0, assignmentIndex).trim();
    if (name !== 'source' && name !== 'units') continue;
    keywordArgs.set(name, stripTrailingPythonLineComment(arg.slice(assignmentIndex + 1)));
  }
  return keywordArgs;
}

function getMismatchMessage(source: string | undefined, units: string | undefined, messages: LinearMechanismVelocityLintMessages): string | null {
  if (!source || !units) return null;
  if (MOTOR_SOURCE_RE.test(source) && LINEAR_VELOCITY_UNIT_RE.test(units)) return messages.motorRequiresRotational;
  if (MECHANISM_OUTPUT_SOURCE_RE.test(source) && ROTATIONAL_VELOCITY_UNIT_RE.test(units)) {
    return messages.mechanismOutputRequiresLinear;
  }
  return null;
}

export function buildLinearMechanismVelocityDiagnostics(code: string, messages: LinearMechanismVelocityLintMessages): Diagnostic[] {
  const lines = splitCodeLines(code);
  const lineStartOffsets = getCodeLineStartOffsets(code);
  const moduleFlowContexts = collectModuleFlowStatementContexts(code, lines);
  const scannableLineIndexes = new Set<number>();
  for (const { statement } of moduleFlowContexts) {
    if (statement.kind === 'def' || statement.kind === 'class') continue;
    for (let lineIndex = statement.startLineIndex; lineIndex <= statement.endLineIndex; lineIndex++) {
      scannableLineIndexes.add(lineIndex);
    }
  }
  const diagnostics: Diagnostic[] = [];

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    if (!scannableLineIndexes.has(lineIndex)) continue;
    const line = lines[lineIndex] ?? '';
    const lineStartOffset = lineStartOffsets[lineIndex] ?? code.length;
    DIRECT_METHOD_CALL_RE.lastIndex = 0;
    let match: RegExpExecArray | null = null;
    while ((match = DIRECT_METHOD_CALL_RE.exec(line))) {
      const receiverName = match[1] ?? '';
      const actionName = match[2] ?? '';
      const receiverStart = match.index;
      if (!SOURCE_AWARE_VELOCITY_ACTIONS.has(actionName) || isQualifiedReceiver(line, receiverStart)) continue;

      const callOffset = lineStartOffset + receiverStart;
      if (isOffsetInPythonStringOrLineComment(code, callOffset)) continue;
      if (!hasProvenLinearMechanismReceiver(receiverName, lineIndex, receiverStart, lines, moduleFlowContexts)) continue;

      const openParenCharacter = receiverStart + match[0].lastIndexOf('(');
      const callSpan = readBalancedDelimiterSpan(code, lineStartOffset + openParenCharacter, '(', ')', {
        ignoreLineComments: true,
      });
      if (!callSpan) continue;

      const keywordArgs = readExplicitKeywordArgs(callSpan.inner);
      const message = getMismatchMessage(keywordArgs.get('source'), keywordArgs.get('units'), messages);
      if (!message) continue;

      const actionStart = receiverStart + match[0].lastIndexOf(actionName);
      diagnostics.push({
        message,
        severity: DiagnosticSeverity.Error,
        source: LINEAR_MECHANISM_VELOCITY_DIAGNOSTIC_SOURCE,
        code: LINEAR_MECHANISM_VELOCITY_DIAGNOSTIC_CODE,
        range: {
          start: { line: lineIndex, character: actionStart },
          end: { line: lineIndex, character: actionStart + actionName.length },
        },
      });
    }
  }

  return diagnostics;
}

export function mergeLinearMechanismVelocityDiagnostics(
  code: string,
  diagnostics: Diagnostic[],
  messages: LinearMechanismVelocityLintMessages
): Diagnostic[] {
  const withoutPreviousDiagnostics = diagnostics.filter(
    (diagnostic) => diagnostic.source !== LINEAR_MECHANISM_VELOCITY_DIAGNOSTIC_SOURCE || diagnostic.code !== LINEAR_MECHANISM_VELOCITY_DIAGNOSTIC_CODE
  );
  return [...withoutPreviousDiagnostics, ...buildLinearMechanismVelocityDiagnostics(code, messages)];
}
